import React from 'react';
import { useHistory } from 'react-router-dom';
import { UserMachinesContext } from '../contexts/UserMachinesContext';
import {
  Box,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  CircularProgress,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import StorageIcon from '@mui/icons-material/Storage';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SignalWifiStatusbar4BarIcon from '@mui/icons-material/SignalWifiStatusbar4Bar';
import TimerIcon from '@mui/icons-material/Timer';
import InfoIcon from '@mui/icons-material/Info';
import RefreshIcon from '@mui/icons-material/Refresh';
import TerminalIcon from '@mui/icons-material/Terminal';
import { apiProxyService, ApiProxyRequest, ApiProxyResponse } from '../remoteConsole/services/apiProxy';
import { MachineIframe } from '../remoteConsole/components/MachineIframe';
import '../remoteConsole/styles/traffic-lights.css';

const useStyles = makeStyles((theme: any) => ({
  root: {
    padding: '6px',
    fontFamily: '"Courier New", monospace',
    color: '#c9d1d9',
    minHeight: 'calc(100vh - 120px)',
  },
  consoleContainer: {
    background: '#0d1117',
    borderRadius: '6px',
    border: '1px solid #30363d',
    overflow: 'hidden',
    height: 'calc(100vh - 132px)',
    width: 'calc(100% - 12px)',
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
  },
  header: {
    background: 'linear-gradient(135deg, #1f2937 0%, #111827 100%)',
    color: '#c9d1d9',
    padding: '8px 16px',
    borderBottom: '1px solid #30363d',
    boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
    height: '40px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexShrink: 0,
  },
  machineSelector: {
    padding: '32px',
    background: 'linear-gradient(135deg, #0d1117 0%, #161b22 50%, #0d1117 100%)',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '400px',
    position: 'relative',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'radial-gradient(circle at center, rgba(34, 197, 94, 0.05) 0%, transparent 70%)',
      pointerEvents: 'none',
    },
  },
  connectButton: {
    background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
    color: '#0d1117',
    fontWeight: 700,
    fontSize: '15px',
    fontFamily: '"Courier New", monospace',
    textTransform: 'none',
    padding: '14px 32px',
    borderRadius: '8px',
    border: 'none',
    boxShadow: '0 4px 20px rgba(34, 197, 94, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
    transition: 'all 0.3s ease',
    position: 'relative',
    overflow: 'hidden',
    '&:hover': {
      background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)',
      boxShadow: '0 6px 30px rgba(34, 197, 94, 0.6), inset 0 1px 0 rgba(255, 255, 255, 0.3)',
      transform: 'translateY(-2px)',
    },
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: '-100%',
      width: '100%',
      height: '100%',
      background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)',
      transition: 'left 0.5s',
    },
    '&:hover::before': {
      left: '100%',
    },
  },
  connectedState: {
    padding: '8px',
    background: '#0d1117',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    minHeight: 0,
  },
  sessionCard: {
    background: 'rgba(22, 27, 34, 0.95)',
    border: '1px solid #444c56',
    borderRadius: '6px',
    padding: '8px 12px',
    marginBottom: '8px',
    backdropFilter: 'blur(12px)',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.4)',
  },
  vueAppContainer: {
    background: '#0d1117',
    borderRadius: '6px',
    padding: 0,
    flex: 1,
    border: '2px solid #30363d',
    overflow: 'hidden',
    position: 'relative',
    boxShadow: '0 0 30px rgba(34, 197, 94, 0.15), inset 0 0 30px rgba(0, 0, 0, 0.2)',
    minHeight: 0,
  },
  statusIndicator: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '4px 8px',
    background: 'rgba(48, 54, 61, 0.6)',
    borderRadius: '4px',
    border: '1px solid rgba(68, 76, 86, 0.5)',
  },
  disconnectButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '4px 8px',
    background: 'rgba(48, 54, 61, 0.6)',
    borderRadius: '4px',
    border: '1px solid rgba(68, 76, 86, 0.5)',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    '&:hover': {
      background: 'rgba(248, 113, 113, 0.15)',
      border: '1px solid rgba(248, 113, 113, 0.4)',
      transform: 'translateY(-1px)',
    },
  },
  connectedChip: {
    background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
    color: '#0d1117',
    fontWeight: 600,
    fontSize: '0.7rem',
    fontFamily: '"Courier New", monospace',
    height: '20px',
    boxShadow: '0 0 10px rgba(34, 197, 94, 0.3)',
  },
  footer: {
    position: 'absolute',
    bottom: '12px',
    left: '12px',
    fontSize: '13px',
    color: '#f0f6fc',
    fontFamily: '"Courier New", monospace',
    background: 'rgba(22, 27, 34, 0.3)',
    padding: '8px 12px',
    borderRadius: '6px',
    border: '1px solid rgba(68, 76, 86, 0.3)',
    backdropFilter: 'blur(8px)',
    boxShadow: '0 1px 4px rgba(0, 0, 0, 0.2)',
    fontWeight: 600,
    transition: 'opacity 0.3s ease',
    opacity: 1,
    '&.hidden': {
      opacity: 0,
      pointerEvents: 'none',
    },
  },
  machineInfo: {
    background: 'rgba(33, 38, 45, 0.95)',
    borderRadius: '8px',
    padding: '16px',
    marginTop: '16px',
    border: '2px solid #444c56',
    color: '#f0f6fc',
    backdropFilter: 'blur(8px)',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.4)',
  },
  modernCard: {
    background: 'rgba(13, 17, 23, 0.9)',
    borderRadius: '6px',
    padding: theme.spacing(2),
    border: '1px solid #30363d',
    transition: 'all 0.3s ease',
    boxShadow: '0 0 15px rgba(34, 197, 94, 0.1)',
    color: '#c9d1d9',
    backdropFilter: 'blur(10px)',
    '&:hover': {
      boxShadow: '0 0 25px rgba(34, 197, 94, 0.25)',
      transform: 'translateY(-2px)',
      border: '1px solid #22c55e',
    },
  },
  pulsingDot: {
    animation: '$pulse 2s infinite',
  },
  '@keyframes pulse': {
    '0%, 100%': { opacity: 1 },
    '50%': { opacity: 0.5 },
  },
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(13, 17, 23, 0.95)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 5,
    backdropFilter: 'blur(10px)',
  },
  loadingSpinner: {
    width: '40px',
    height: '40px',
    border: '3px solid #30363d',
    borderTop: '3px solid #22c55e',
    borderRadius: '50%',
    animation: '$spin 1s linear infinite',
    marginBottom: '16px',
  },
  '@keyframes spin': {
    '0%': { transform: 'rotate(0deg)' },
    '100%': { transform: 'rotate(360deg)' },
  },
}));

const RemoteConsolePage: React.FunctionComponent = () => {
  const classes = useStyles();
  const history = useHistory();

  // State management
  const [selectedMachine, setSelectedMachine] = React.useState('');
  const [isConnected, setIsConnected] = React.useState(false);
  const [isConnecting, setIsConnecting] = React.useState(false);
  const [sessionTime, setSessionTime] = React.useState('00:00');
  const [vueAppLoaded, setVueAppLoaded] = React.useState(false);
  const [showLoading, setShowLoading] = React.useState(false);
  const [showDisconnectDialog, setShowDisconnectDialog] = React.useState(false);
  const [isFooterHidden, setIsFooterHidden] = React.useState(false);

  // Machine data from context
  const machinesContext = React.useContext(UserMachinesContext);
  const { accountMachines = [], machinesLoading, fetchUserMachines } = machinesContext;

  // Transform API data to match component expectations
  const machines = accountMachines.map((machine: any) => ({
    id: machine.serial_number,
    name: machine.machine_name,
    serialNumber: machine.serial_number,
    city: machine.city || 'Unknown',
    systemType: machine.system_type,
    uiVersion: machine.indusoft_project,
    connectionState: machine.connection_state,
    lastOnlineDate: machine.last_online_date,
    subscriptionActive: machine.subscription_active,
    status: machine.connection_state === 'online' ? 'Online' : 'Offline',
    location: machine.city || 'Unknown Location',
  }));

  const selectedMachineData = machines.find((m) => m.id === selectedMachine);

  // Helper function to calculate time since last online
  const getTimeSinceLastOnline = (lastOnlineDate: string) => {
    if (!lastOnlineDate) return 'Unknown';
    const now = new Date();
    const lastOnline = new Date(lastOnlineDate);
    const diffMs = now.getTime() - lastOnline.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

    if (diffDays > 0) return `${diffDays}d ${diffHours}h ago`;
    if (diffHours > 0) return `${diffHours}h ${diffMinutes}m ago`;
    return `${diffMinutes}m ago`;
  };

  // Fetch machines on component mount
  React.useEffect(() => {
    if (fetchUserMachines) {
      fetchUserMachines('use_cloudflare_channel=true');
    }
  }, []); // Empty dependency array to run only once on mount

  // Timer effect for session duration
  React.useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isConnected) {
      let seconds = 0;
      interval = setInterval(() => {
        seconds++;
        const minutes = Math.floor(seconds / 60);
        const secs = seconds % 60;
        setSessionTime(`${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isConnected]);

  const clearIframeCache = () => {
    // Clear only iframe-specific cache and force reload
    try {
      const iframe = document.querySelector('iframe');
      if (iframe) {
        // Force iframe to reload with cache-busting parameter
        const currentSrc = iframe.src;
        const separator = currentSrc.includes('?') ? '&' : '?';
        const cacheBuster = `_cb=${Date.now()}`;
        iframe.src = `${currentSrc}${separator}${cacheBuster}`;

        console.log('🔄 Forced iframe reload with cache busting');
      }

      // Clear only machine-specific localStorage keys (preserve React app auth)
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (
          key &&
          (key.includes('machine_') ||
            key.includes('console_') ||
            key.includes('vue_') ||
            key.includes('autobulk_') ||
            key.includes('autotreat_'))
        ) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach((key) => localStorage.removeItem(key));

      // Clear machine-specific sessionStorage keys
      const sessionKeysToRemove = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (
          key &&
          (key.includes('machine_') ||
            key.includes('console_') ||
            key.includes('vue_') ||
            key.includes('autobulk_') ||
            key.includes('autotreat_'))
        ) {
          sessionKeysToRemove.push(key);
        }
      }
      sessionKeysToRemove.forEach((key) => sessionStorage.removeItem(key));

      console.log('🧹 Cleared iframe-specific cache only');
    } catch (error) {
      console.warn('⚠️ Could not clear iframe cache:', error);
    }
  };

  const handleConnect = async () => {
    if (!selectedMachine) return;

    const machine = machines.find((m) => m.id === selectedMachine);
    if (!machine) return;

    // Check subscription status
    if (!machine.subscriptionActive) {
      alert('Cannot connect: Machine subscription is not active. Please contact support.');
      return;
    }

    // Clear iframe cache before connecting to ensure fresh session
    clearIframeCache();

    setIsConnecting(true);
    setShowLoading(true);

    // Simulate connection delay
    await new Promise((resolve) => setTimeout(resolve, 2000));

    setIsConnected(true);
    setIsConnecting(false);
    setShowLoading(false);
    // Simulate iframe loading
    setTimeout(() => setVueAppLoaded(true), 1500);
  };

  const handleDisconnectClick = () => {
    setShowDisconnectDialog(true);
  };

  const handleDisconnect = () => {
    // Clear iframe cache when disconnecting to prevent cached login state
    clearIframeCache();

    setIsConnected(false);
    setSelectedMachine('');
    setVueAppLoaded(false);
    setSessionTime('00:00');
    setShowDisconnectDialog(false);
  };

  const handleDisconnectCancel = () => {
    setShowDisconnectDialog(false);
  };

  // Traffic light handlers
  const handleRedTrafficLight = () => {
    handleDisconnectClick();
  };

  const handleYellowTrafficLight = () => {
    // Return console to normal size
    const consoleContainer = document.querySelector('.console-container');
    if (consoleContainer) {
      consoleContainer.classList.remove('console-fullscreen');
      console.log('🟡 Returned console to normal size');
    }
  };

  const handleGreenTrafficLight = () => {
    // Expand console to fullscreen mode
    const consoleContainer = document.querySelector('.console-container');
    if (consoleContainer) {
      consoleContainer.classList.add('console-fullscreen');
      console.log('🖥️ Expanded console to fullscreen mode');
    }
  };

  // Pre-connection state
  if (!isConnected) {
    return (
      <Box className={classes.root}>
        <Box className={`${classes.consoleContainer} console-container`}>
          {/* Console Header */}
          <div className={classes.header}>
            <Box display="flex" alignItems="center" gap={1}>
              <Box width={8} height={8} borderRadius="50%" bgcolor="#22c55e" className={classes.pulsingDot} />
              <TerminalIcon style={{ fontSize: '16px', color: '#22c55e', marginRight: '4px' }} />
              <Typography
                variant="h6"
                style={{ fontWeight: 600, fontSize: '14px', color: '#c9d1d9', fontFamily: '"Courier New", monospace' }}
              >
                AgConnex Remote Console
              </Typography>
              <Typography variant="caption" style={{ color: '#8b949e', fontSize: '12px' }}>
                → Machine Selection
              </Typography>
            </Box>
            <Box display="flex" gap={0.5}>
              <Box width={12} height={12} borderRadius="50%" bgcolor="#ff5f57" sx={{ cursor: 'pointer' }} />
              <Box width={12} height={12} borderRadius="50%" bgcolor="#ffbd2e" sx={{ cursor: 'pointer' }} />
              <Box width={12} height={12} borderRadius="50%" bgcolor="#28ca42" sx={{ cursor: 'pointer' }} />
            </Box>
          </div>

          {/* Machine Selector */}
          <Box className={classes.machineSelector}>
            <Box display="flex" alignItems="center" gap={2} mb={4}>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                width={40}
                height={40}
                borderRadius="50%"
                bgcolor="rgba(34, 197, 94, 0.1)"
                border="1px solid rgba(34, 197, 94, 0.3)"
              >
                <TerminalIcon style={{ color: '#22c55e', fontSize: '22px' }} />
              </Box>
              <Typography
                variant="h5"
                style={{ color: '#f0f6fc', fontSize: '20px', fontWeight: 700, fontFamily: '"Courier New", monospace' }}
              >
                Initialize Remote Connection
              </Typography>
            </Box>

            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={6}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel
                    sx={{
                      color: '#e6edf3',
                      fontSize: '15px',
                      fontFamily: '"Courier New", monospace',
                      fontWeight: 500,
                      '&.Mui-focused': {
                        color: '#22c55e',
                      },
                    }}
                  >
                    Select Target Machine
                  </InputLabel>
                  <Select
                    value={selectedMachine}
                    onChange={(e) => setSelectedMachine(e.target.value as string)}
                    label="Select Target Machine"
                    sx={{
                      color: '#f0f6fc',
                      fontFamily: '"Courier New", monospace',
                      fontSize: '15px',
                      fontWeight: 500,
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#444c56',
                        borderWidth: '2px',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#22c55e',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#22c55e',
                        borderWidth: '2px',
                      },
                      '& .MuiSelect-select': {
                        backgroundColor: 'rgba(22, 27, 34, 0.9)',
                        borderRadius: '6px',
                        padding: '12px 14px',
                      },
                      '& .MuiInputLabel-root': {
                        color: '#e6edf3',
                      },
                      '& .MuiInputLabel-root.Mui-focused': {
                        color: '#22c55e',
                      },
                    }}
                    MenuProps={{
                      PaperProps: {
                        style: {
                          backgroundColor: '#21262d',
                          border: '2px solid #444c56',
                          color: '#f0f6fc',
                          borderRadius: '8px',
                          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.7)',
                          marginTop: '4px',
                        },
                      },
                    }}
                  >
                    {machinesLoading ? (
                      <MenuItem disabled>
                        <CircularProgress size={20} style={{ color: '#22c55e', marginRight: '8px' }} />
                        Loading machines...
                      </MenuItem>
                    ) : machines.length === 0 ? (
                      <MenuItem disabled>
                        <Typography style={{ color: '#8b949e', fontStyle: 'italic' }}>No machines available</Typography>
                      </MenuItem>
                    ) : (
                      machines.map((machine) => (
                        <MenuItem
                          key={machine.id}
                          value={machine.id}
                          disabled={!machine.subscriptionActive}
                          sx={{
                            color: machine.subscriptionActive ? '#f0f6fc' : '#8b949e',
                            fontFamily: '"Courier New", monospace',
                            fontSize: '14px',
                            padding: '12px 16px',
                            opacity: machine.subscriptionActive ? 1 : 0.6,
                            '&:hover': {
                              backgroundColor: machine.subscriptionActive
                                ? 'rgba(34, 197, 94, 0.1)'
                                : 'rgba(248, 113, 113, 0.1)',
                            },
                            '&.Mui-selected': {
                              backgroundColor: 'rgba(34, 197, 94, 0.2)',
                              '&:hover': {
                                backgroundColor: 'rgba(34, 197, 94, 0.25)',
                              },
                            },
                          }}
                        >
                          <Box display="flex" alignItems="center" gap={2} width="100%">
                            <Box
                              width={10}
                              height={10}
                              borderRadius="50%"
                              bgcolor={machine.status === 'Online' ? '#22c55e' : '#f87171'}
                            />
                            <Box flex={1}>
                              <Box display="flex" alignItems="center" gap={1}>
                                <Typography
                                  style={{
                                    color: machine.subscriptionActive ? '#f0f6fc' : '#8b949e',
                                    fontSize: '15px',
                                    fontWeight: 600,
                                  }}
                                >
                                  {machine.name}
                                </Typography>
                                {!machine.subscriptionActive && (
                                  <Chip
                                    label="INACTIVE"
                                    size="small"
                                    style={{
                                      backgroundColor: '#f87171',
                                      color: '#0d1117',
                                      fontSize: '10px',
                                      height: '18px',
                                    }}
                                  />
                                )}
                              </Box>
                              <Typography style={{ color: '#c9d1d9', fontSize: '12px' }}>
                                {machine.city} • {machine.systemType}
                              </Typography>
                              <Typography style={{ color: '#8b949e', fontSize: '11px' }}>
                                {machine.serialNumber} • UI: {machine.uiVersion} • {machine.status}
                                {machine.status === 'Offline' && machine.lastOnlineDate && (
                                  <span> • Last seen {getTimeSinceLastOnline(machine.lastOnlineDate)}</span>
                                )}
                              </Typography>
                            </Box>
                          </Box>
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <Button
                  className={classes.connectButton}
                  variant="contained"
                  size="large"
                  disabled={!selectedMachine || isConnecting}
                  onClick={handleConnect}
                  startIcon={isConnecting ? <RefreshIcon className={classes.pulsingDot} /> : <CheckCircleIcon />}
                  fullWidth
                >
                  {isConnecting ? 'Establishing Connection...' : 'Connect to Machine'}
                </Button>
                {isConnecting && (
                  <Box display="flex" alignItems="center" justifyContent="center" mt={1}>
                    <CircularProgress size={20} style={{ color: '#22c55e' }} />
                    <Typography variant="caption" style={{ color: '#8b949e', marginLeft: '8px', fontSize: '11px' }}>
                      Initializing secure connection...
                    </Typography>
                  </Box>
                )}
              </Grid>
            </Grid>

            {/* Machine Info Preview */}
            {selectedMachineData && (
              <Box className={classes.machineInfo}>
                <Typography
                  variant="h6"
                  style={{
                    color: '#f0f6fc',
                    fontSize: '16px',
                    fontWeight: 700,
                    marginBottom: '12px',
                    fontFamily: '"Courier New", monospace',
                  }}
                >
                  Target: {selectedMachineData.name}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <Typography variant="body2" style={{ color: '#e6edf3', fontSize: '13px', fontWeight: 600 }}>
                        Serial:
                      </Typography>
                      <Typography variant="body2" style={{ color: '#8b949e', fontSize: '13px' }}>
                        {selectedMachineData.serialNumber}
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <Typography variant="body2" style={{ color: '#e6edf3', fontSize: '13px', fontWeight: 600 }}>
                        Location:
                      </Typography>
                      <Typography variant="body2" style={{ color: '#8b949e', fontSize: '13px' }}>
                        {selectedMachineData.city}
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <Typography variant="body2" style={{ color: '#e6edf3', fontSize: '13px', fontWeight: 600 }}>
                        Type:
                      </Typography>
                      <Typography variant="body2" style={{ color: '#8b949e', fontSize: '13px' }}>
                        {selectedMachineData.systemType}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <Typography variant="body2" style={{ color: '#e6edf3', fontSize: '13px', fontWeight: 600 }}>
                        UI Version:
                      </Typography>
                      <Typography variant="body2" style={{ color: '#8b949e', fontSize: '13px' }}>
                        {selectedMachineData.uiVersion}
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <Typography variant="body2" style={{ color: '#e6edf3', fontSize: '13px', fontWeight: 600 }}>
                        Status:
                      </Typography>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Box
                          width={6}
                          height={6}
                          borderRadius="50%"
                          bgcolor={selectedMachineData.status === 'Online' ? '#22c55e' : '#f87171'}
                        />
                        <Typography
                          variant="body2"
                          style={{
                            color: selectedMachineData.status === 'Online' ? '#22c55e' : '#f87171',
                            fontSize: '13px',
                            fontWeight: 600,
                          }}
                        >
                          {selectedMachineData.status}
                        </Typography>
                      </Box>
                    </Box>
                    {selectedMachineData.status === 'Offline' && selectedMachineData.lastOnlineDate && (
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <Typography variant="body2" style={{ color: '#e6edf3', fontSize: '13px', fontWeight: 600 }}>
                          Last Online:
                        </Typography>
                        <Typography variant="body2" style={{ color: '#8b949e', fontSize: '13px' }}>
                          {getTimeSinceLastOnline(selectedMachineData.lastOnlineDate)}
                        </Typography>
                      </Box>
                    )}
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="body2" style={{ color: '#e6edf3', fontSize: '13px', fontWeight: 600 }}>
                        Subscription:
                      </Typography>
                      <Chip
                        label={selectedMachineData.subscriptionActive ? 'ACTIVE' : 'INACTIVE'}
                        size="small"
                        style={{
                          backgroundColor: selectedMachineData.subscriptionActive ? '#22c55e' : '#f87171',
                          color: '#0d1117',
                          fontSize: '10px',
                          height: '18px',
                        }}
                      />
                    </Box>
                  </Grid>
                </Grid>
                {!selectedMachineData.subscriptionActive && (
                  <Box
                    mt={2}
                    p={2}
                    bgcolor="rgba(248, 113, 113, 0.1)"
                    borderRadius={1}
                    border="1px solid rgba(248, 113, 113, 0.3)"
                  >
                    <Typography style={{ color: '#f87171', fontSize: '12px', fontWeight: 600 }}>
                      ⚠️ Connection Unavailable
                    </Typography>
                    <Typography style={{ color: '#c9d1d9', fontSize: '11px' }}>
                      This machine's subscription is inactive. Contact support to restore access.
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    );
  }

  // Connected state
  return (
    <Box className={classes.root}>
      <Box className={`${classes.consoleContainer} console-container`}>
        {/* Console Header - Connected */}
        <Box className={classes.header}>
          <Box display="flex" alignItems="center" gap={1}>
            <Box width={8} height={8} borderRadius="50%" bgcolor="#22c55e" className={classes.pulsingDot} />
            <TerminalIcon style={{ fontSize: '16px', color: '#22c55e', marginRight: '4px' }} />
            <Typography
              variant="h6"
              style={{ fontWeight: 600, fontSize: '14px', color: '#c9d1d9', fontFamily: '"Courier New", monospace' }}
            >
              AgConnex Remote Console
            </Typography>
            <Typography variant="caption" style={{ color: '#8b949e', fontSize: '12px' }}>
              → {selectedMachineData?.name} • {sessionTime}
            </Typography>
            <Chip label="CONNECTED" className={classes.connectedChip} size="small" />
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <Box className={classes.disconnectButton} onClick={handleDisconnectClick}>
              <Typography style={{ color: '#f0f6fc', fontSize: '12px', fontWeight: 500 }}>Disconnect</Typography>
            </Box>
            <Box display="flex" gap={0.5}>
              <Box
                width={12}
                height={12}
                borderRadius="50%"
                bgcolor="#ff5f57"
                className="traffic-light red"
                sx={{ cursor: 'pointer' }}
                onClick={handleRedTrafficLight}
                title="Close Connection"
              />
              <Box
                width={12}
                height={12}
                borderRadius="50%"
                bgcolor="#ffbd2e"
                className="traffic-light yellow"
                sx={{ cursor: 'pointer' }}
                onClick={handleYellowTrafficLight}
                title="Return to Normal Size"
              />
              <Box
                width={12}
                height={12}
                borderRadius="50%"
                bgcolor="#28ca42"
                className="traffic-light green"
                sx={{ cursor: 'pointer' }}
                onClick={handleGreenTrafficLight}
                title="Fullscreen Mode"
              />
            </Box>
          </Box>
        </Box>

        {/* Connected Content */}
        <Box className={classes.connectedState}>
          {/* Console Status Bar */}
          <Box className={classes.sessionCard}>
            <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
              <Box display="flex" alignItems="center" gap={2}>
                <Box className={classes.statusIndicator}>
                  <Box width={6} height={6} borderRadius="50%" bgcolor="#22c55e" className={classes.pulsingDot} />
                  <Typography style={{ color: '#f0f6fc', fontSize: '12px', fontWeight: 500 }}>
                    Session: {sessionTime}
                  </Typography>
                </Box>
                <Box className={classes.statusIndicator}>
                  <SignalWifiStatusbar4BarIcon style={{ color: '#22c55e', fontSize: '14px' }} />
                  <Typography style={{ color: '#f0f6fc', fontSize: '12px', fontWeight: 500 }}>Strong</Typography>
                </Box>
                <Box className={classes.statusIndicator}>
                  <InfoIcon style={{ color: '#22c55e', fontSize: '14px' }} />
                  <Typography style={{ color: '#f0f6fc', fontSize: '12px', fontWeight: 500 }}>
                    {vueAppLoaded ? 'Remote Active' : 'Loading...'}
                  </Typography>
                </Box>
              </Box>
              <IconButton
                onClick={() => window.location.reload()}
                style={{ color: '#8b949e', padding: '4px' }}
                size="small"
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          {/* Remote Console Container */}
          <Box
            className={classes.vueAppContainer}
            onMouseEnter={() => setIsFooterHidden(true)}
            onMouseLeave={() => setIsFooterHidden(false)}
          >
            {showLoading && (
              <Box className={classes.loadingOverlay}>
                <div className={classes.loadingSpinner}></div>
                <Typography style={{ fontSize: '15px', color: '#e6edf3', fontWeight: 500 }}>
                  Establishing remote connection...
                </Typography>
              </Box>
            )}

            {selectedMachineData && (
              <MachineIframe
                machineId={selectedMachineData.id}
                indusoft_project={selectedMachineData.uiVersion}
                height="100%"
                width="100%"
              />
            )}

            <Box className={`${classes.footer} ${isFooterHidden ? 'hidden' : ''}`}>
              Remote Session • {selectedMachineData?.location}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Disconnect Confirmation Dialog */}
      <Dialog
        open={showDisconnectDialog}
        onClose={handleDisconnectCancel}
        PaperProps={{
          style: {
            backgroundColor: '#161b22',
            border: '1px solid #444c56',
            borderRadius: '8px',
            color: '#f0f6fc',
          },
        }}
      >
        <DialogTitle style={{ color: '#f0f6fc', fontFamily: '"Courier New", monospace' }}>
          Confirm Disconnect
        </DialogTitle>
        <DialogContent>
          <DialogContentText style={{ color: '#c9d1d9', fontFamily: '"Courier New", monospace' }}>
            Are you sure you want to disconnect from {selectedMachineData?.name}? This will end your remote session.
          </DialogContentText>
        </DialogContent>
        <DialogActions style={{ padding: '16px 24px' }}>
          <Button
            onClick={handleDisconnectCancel}
            style={{
              color: '#8b949e',
              borderColor: '#444c56',
              fontFamily: '"Courier New", monospace',
              textTransform: 'none',
            }}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={handleDisconnect}
            style={{
              backgroundColor: '#f87171',
              color: '#0d1117',
              fontFamily: '"Courier New", monospace',
              textTransform: 'none',
              fontWeight: 600,
            }}
            variant="contained"
          >
            Disconnect
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RemoteConsolePage;
